"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useDebounce } from "react-use";
import IdentitySearch, { IdentitySearchSize } from "@/components/utils/input/identity/IdentitySearch";
import type { ApiCreateGroupDescription } from "@/generated/models/ApiCreateGroupDescription";
import { ApiXTdhGrantStatus } from "@/generated/models/ApiXTdhGrantStatus";
import { useXtdhGrantQuery } from "@/hooks/useXtdhGrantQuery";
import { useXtdhGrantsSearchQuery } from "@/hooks/useXtdhGrantsSearchQuery";
import GroupCreateXtdhGrantRow from "@/components/groups/page/create/config/xtdh-grant/subcomponents/GroupCreateXtdhGrantRow";
import { isSelectableNonGrantedStatus, toShortGrantId } from "@/components/groups/page/create/config/xtdh-grant/utils";
const STATUS_OPTIONS = [ApiXTdhGrantStatus.Granted, ApiXTdhGrantStatus.Pending, ApiXTdhGrantStatus.Disabled, ApiXTdhGrantStatus.Failed] as const;
const STATUS_LABELS: Record<ApiXTdhGrantStatus, string> = {
  [ApiXTdhGrantStatus.Granted]: "Granted",
  [ApiXTdhGrantStatus.Pending]: "Pending",
  [ApiXTdhGrantStatus.Disabled]: "Revoked",
  [ApiXTdhGrantStatus.Failed]: "Failed"
};
export default function CreateWaveInlineGroupXtdhGrant(t0) {
  const $ = _c(82);
  const {
    beneficiaryGrantId,
    setBeneficiaryGrantId
  } = t0;
  let t1;
  if ($[0] !== beneficiaryGrantId) {
    t1 = beneficiaryGrantId?.trim() ?? "";
    $[0] = beneficiaryGrantId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedGrantId = t1;
  const hasSelectedGrant = normalizedGrantId.length > 0;
  const [showGrantFinder, setShowGrantFinder] = useState(false);
  const [lookupGrantId, setLookupGrantId] = useState(hasSelectedGrant ? normalizedGrantId : null);
  const [selectedGrantor, setSelectedGrantor] = useState(null);
  const [targetCollectionInput, setTargetCollectionInput] = useState("");
  const [targetCollectionFilter, setTargetCollectionFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(ApiXTdhGrantStatus.Granted);
  let t2;
  if ($[2] !== targetCollectionInput) {
    t2 = () => setTargetCollectionFilter(targetCollectionInput.trim());
    $[2] = targetCollectionInput;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== targetCollectionInput) {
    t3 = [targetCollectionInput];
    $[4] = targetCollectionInput;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useDebounce(t2, 250, t3);
  let t4;
  if ($[6] !== hasSelectedGrant || $[7] !== normalizedGrantId) {
    t4 = () => setLookupGrantId(hasSelectedGrant ? normalizedGrantId : null);
    $[6] = hasSelectedGrant;
    $[7] = normalizedGrantId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== hasSelectedGrant || $[10] !== normalizedGrantId) {
    t5 = [hasSelectedGrant, normalizedGrantId];
    $[9] = hasSelectedGrant;
    $[10] = normalizedGrantId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useDebounce(t4, 250, t5);
  const t6 = !!lookupGrantId;
  let t7;
  if ($[12] !== lookupGrantId || $[13] !== t6) {
    t7 = {
      grantId: lookupGrantId,
      enabled: t6
    };
    $[12] = lookupGrantId;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    grant,
    isFetching,
    isError,
    errorMessage
  } = useXtdhGrantQuery(t7);
  const t8 = targetCollectionFilter || null;
  let t9;
  if ($[15] !== selectedStatus) {
    t9 = [selectedStatus];
    $[15] = selectedStatus;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== selectedGrantor || $[18] !== showGrantFinder || $[19] !== t8 || $[20] !== t9) {
    t10 = {
      grantor: selectedGrantor,
      targetCollectionName: t8,
      statuses: t9,
      enabled: showGrantFinder,
      pageSize: 20
    };
    $[17] = selectedGrantor;
    $[18] = showGrantFinder;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const {
    grants,
    totalCount,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError: isSearchError,
    errorMessage: searchErrorMessage,
    refetch
  } = useXtdhGrantsSearchQuery(t10);
  const isLookupFresh = lookupGrantId === normalizedGrantId;
  const showLookupError = isLookupFresh && Boolean(lookupGrantId && isError);
  let t11;
  if ($[22] !== grant || $[23] !== isLookupFresh) {
    t11 = isLookupFresh && grant?.status !== undefined && isSelectableNonGrantedStatus(grant.status);
    $[22] = grant;
    $[23] = isLookupFresh;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const showNonGrantedWarning = t11;
  let t12;
  if ($[25] !== setBeneficiaryGrantId) {
    t12 = nextValue => {
      const normalized = nextValue.trim();
      setBeneficiaryGrantId(normalized.length ? normalized : null);
    };
    $[25] = setBeneficiaryGrantId;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const onInputChange = t12;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setSelectedGrantor(null);
      setTargetCollectionInput("");
      setTargetCollectionFilter("");
      setSelectedStatus(ApiXTdhGrantStatus.Granted);
    };
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const onResetFilters = t13;
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">xTDH Grant Beneficiary</p><p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-iron-400">Require identities to be beneficiaries of a selected xTDH grant.</p></div>;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== onInputChange) {
    t15 = event => onInputChange(event.target.value);
    $[29] = onInputChange;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== normalizedGrantId || $[32] !== t15) {
    t16 = <input id="create-wave-inline-xtdh-grant-id" type="text" value={normalizedGrantId} onChange={t15} placeholder=" " className="tw-peer tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-py-2.5 tw-text-base tw-font-medium tw-text-iron-50 tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 hover:tw-ring-iron-650 focus:tw-bg-iron-900 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 sm:tw-text-sm" />;
    $[31] = normalizedGrantId;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <label htmlFor="create-wave-inline-xtdh-grant-id" className="tw-absolute tw-start-1 tw-top-2 tw-z-10 tw-origin-[0] -tw-translate-y-4 tw-scale-75 tw-transform tw-cursor-text tw-rounded-lg tw-bg-iron-900 tw-px-2 tw-text-sm tw-font-medium tw-text-iron-500 tw-duration-300 peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-scale-100 peer-focus:tw-top-2 peer-focus:-tw-translate-y-4 peer-focus:tw-scale-75 peer-focus:tw-bg-iron-900 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 rtl:peer-focus:tw-left-auto rtl:peer-focus:tw-translate-x-1/4">Grant ID</label>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16) {
    t18 = <div className="tw-group tw-relative tw-w-full">{t16}{t17}</div>;
    $[35] = t16;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setShowGrantFinder(_temp);
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = showGrantFinder ? "Hide finder" : "Find grant";
  const t21 = `tw-size-4 tw-flex-shrink-0 tw-transition-transform tw-duration-200 ${showGrantFinder ? "tw-rotate-180" : ""}`;
  let t22;
  if ($[38] !== t21) {
    t22 = <ChevronDownIcon aria-hidden="true" className={t21} />;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== showGrantFinder || $[41] !== t20 || $[42] !== t22) {
    t23 = <button type="button" onClick={t19} aria-expanded={showGrantFinder} aria-controls="create-wave-inline-xtdh-grant-finder" className="tw-inline-flex tw-h-[42px] tw-items-center tw-justify-center tw-gap-2 tw-self-end tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 desktop-hover:hover:tw-bg-iron-800">{t20}{t22}</button>;
    $[40] = showGrantFinder;
    $[41] = t20;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t18 || $[45] !== t23) {
    t24 = <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-[minmax(0,1fr)_auto]">{t18}{t23}</div>;
    $[44] = t18;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== isFetching || $[48] !== lookupGrantId) {
    t25 = isFetching && !!lookupGrantId && <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-font-medium tw-text-iron-400">Validating grant...</p>;
    $[47] = isFetching;
    $[48] = lookupGrantId;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== errorMessage || $[51] !== lookupGrantId || $[52] !== showLookupError) {
    t26 = showLookupError && <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-solid tw-border-red/30 tw-bg-red/10 tw-p-3"><p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-red">{errorMessage ?? "Unable to resolve grant ID."}</p><p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-red/90">The ID will still be submitted as entered:{" "}<span className="tw-font-semibold">{toShortGrantId(lookupGrantId)}</span></p></div>;
    $[50] = errorMessage;
    $[51] = lookupGrantId;
    $[52] = showLookupError;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== grant || $[55] !== isLookupFresh) {
    t27 = isLookupFresh && !!grant && <GroupCreateXtdhGrantRow grant={grant} isSelected={true} interactive={false} className="tw-mt-3" />;
    $[54] = grant;
    $[55] = isLookupFresh;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== showNonGrantedWarning) {
    t28 = showNonGrantedWarning && <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-solid tw-border-amber-300/30 tw-bg-amber-300/10 tw-p-3"><p className="tw-m-0 tw-text-xs tw-font-medium tw-text-amber-300">Selected grant status is not GRANTED. This filter is still allowed and will be submitted.</p></div>;
    $[57] = showNonGrantedWarning;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== fetchNextPage || $[60] !== grants || $[61] !== hasNextPage || $[62] !== isFetchingNextPage || $[63] !== isLoading || $[64] !== isSearchError || $[65] !== normalizedGrantId || $[66] !== refetch || $[67] !== searchErrorMessage || $[68] !== selectedGrantor || $[69] !== selectedStatus || $[70] !== setBeneficiaryGrantId || $[71] !== showGrantFinder || $[72] !== targetCollectionInput || $[73] !== totalCount) {
    t29 = showGrantFinder && <div id="create-wave-inline-xtdh-grant-finder" className="tw-mt-6 tw-space-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-6"><div className="tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-2"><IdentitySearch label="Grantor" size={IdentitySearchSize.SM} identity={selectedGrantor} setIdentity={identity => setSelectedGrantor(identity ? identity.toLowerCase() : null)} /><input type="text" value={targetCollectionInput} onChange={event_0 => setTargetCollectionInput(event_0.target.value)} placeholder="Collection name" aria-label="Collection name" className="tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-py-2.5 tw-text-base tw-text-iron-50 tw-ring-1 tw-ring-inset tw-ring-iron-700 placeholder:tw-text-iron-500 focus:tw-ring-primary-400 sm:tw-text-sm" /></div><div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3"><div className="tw-min-w-0 tw-flex-1"><span className="tw-mb-2 tw-block tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-500">Filter by status</span><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{STATUS_OPTIONS.map(status => {
              const isActive = selectedStatus === status;
              return <button key={status} type="button" aria-pressed={isActive} onClick={() => setSelectedStatus(status)} className={`tw-rounded-md tw-border tw-border-solid tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition tw-duration-200 ${isActive ? "tw-border-primary-400 tw-bg-primary-400/20 tw-text-primary-300" : "tw-border-iron-700 tw-bg-iron-900 tw-text-iron-300 desktop-hover:hover:tw-border-iron-600"}`}>{STATUS_LABELS[status]}</button>;
            })}</div></div><button type="button" onClick={onResetFilters} className="tw-h-[42px] tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 desktop-hover:hover:tw-bg-iron-800">Clear filters</button></div><div className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/40"><div className="tw-flex tw-items-center tw-justify-between tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-4 tw-py-2.5"><p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-400">Results</p><p className="tw-m-0 tw-text-xs tw-text-iron-500">{totalCount} total</p></div><div className="tw-max-h-80 tw-overflow-y-auto tw-p-2 sm:tw-p-3">{isLoading && !grants.length && <p className="tw-m-0 tw-rounded-lg tw-bg-iron-900 tw-p-4 tw-text-sm tw-text-iron-400">Loading grants...</p>}{isSearchError && !grants.length && <div className="tw-rounded-lg tw-border tw-border-solid tw-border-red/30 tw-bg-red/10 tw-p-4"><p className="tw-m-0 tw-text-sm tw-text-red">{searchErrorMessage ?? "Unable to load grants."}</p><button type="button" onClick={() => {
              refetch().catch(_temp2);
            }} className="tw-mt-3 tw-rounded-md tw-border tw-border-solid tw-border-red/40 tw-bg-red/20 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-red">Retry</button></div>}{!isLoading && !isSearchError && !grants.length && <p className="tw-m-0 tw-rounded-lg tw-bg-iron-900 tw-p-4 tw-text-sm tw-text-iron-400">No grants matched the selected filters.</p>}{!!grants.length && <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">{grants.map(grantItem => <GroupCreateXtdhGrantRow key={grantItem.id} grant={grantItem} isSelected={normalizedGrantId === grantItem.id} interactive={true} asListItem={true} onSelect={selectedGrant => {
              setBeneficiaryGrantId(selectedGrant.id);
              setLookupGrantId(selectedGrant.id);
            }} />)}</ul>}</div>{hasNextPage && <div className="tw-border-t tw-border-solid tw-border-iron-800 tw-p-3"><button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-bg-iron-800">{isFetchingNextPage ? "Loading..." : "Load more"}</button></div>}</div></div>;
    $[59] = fetchNextPage;
    $[60] = grants;
    $[61] = hasNextPage;
    $[62] = isFetchingNextPage;
    $[63] = isLoading;
    $[64] = isSearchError;
    $[65] = normalizedGrantId;
    $[66] = refetch;
    $[67] = searchErrorMessage;
    $[68] = selectedGrantor;
    $[69] = selectedStatus;
    $[70] = setBeneficiaryGrantId;
    $[71] = showGrantFinder;
    $[72] = targetCollectionInput;
    $[73] = totalCount;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] !== t24 || $[76] !== t25 || $[77] !== t26 || $[78] !== t27 || $[79] !== t28 || $[80] !== t29) {
    t30 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-3 tw-shadow sm:tw-p-5">{t14}{t24}{t25}{t26}{t27}{t28}{t29}</div>;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  return t30;
}
function _temp2() {}
function _temp(current) {
  return !current;
}